import * as React from 'react';
import type {
	DOMConversionMap,
	DOMConversionOutput,
	DOMExportOutput,
	EditorConfig,
	LexicalNode,
	NodeKey,
	SerializedLexicalNode,
	Spread,
} from 'lexical';
import { $applyNodeReplacement, DecoratorNode } from 'lexical';
import { Suspense } from 'react';
import { getEnv, hasEnv } from "../../helpers/env.ts";
import { isUrlRelative } from "../../helpers/utils";
import { clsx } from 'clsx';
import InlineImageComponent from '../plugins/InlineImagePlugin/InlineImageComponent';
//const InlineImageComponent = lazy(() => import('../plugins/InlineImagePlugin/InlineImageComponent'));

export type Position = 'left'|'right'|'full'|undefined;

export interface InlineImagePayload {
	src: string;
	altText: string;
	width?: number;
	height?: number;
	position?: Position;
	key?: NodeKey;
	classes?: string
}

function convertInlineImageElement(domNode: Node): null|DOMConversionOutput {
	if (domNode instanceof HTMLImageElement) {
		const {alt: altText, src, width, height, className: classes} = domNode;

		const node = $createInlineImageNode({altText, height, src, width});
		return {node};
	}
	return null;
}

export type SerializedInlineImageNode = Spread<
	{
		altText: string;
		height?: number;
		src: string;
		width?: number;
		position?: Position;
		classes?: string;
	},
	SerializedLexicalNode
>;

export class InlineImageNode extends DecoratorNode<JSX.Element> {
	__src: string;
	__altText: string;
	__width: 'inherit'|number;
	__height: 'inherit'|number;
	__position: Position;
	__classes: string;

	static getType(): string {
		return 'inline-image';
	}

	static clone(node: InlineImageNode): InlineImageNode {
		return new InlineImageNode(
			node.__src,
			node.__altText,
			node.__position,
			node.__width,
			node.__height,
			node.__key,
			node.__classes,
		);
	}

	static importJSON(serializedNode: SerializedInlineImageNode): InlineImageNode {
		const {altText, height, width, src, position, classes} = serializedNode;
		// console.log(serializedNode.src, serializedNode.width, serializedNode.width, serializedNode);
		return $createInlineImageNode({altText, height,	position, src, width, classes});
	}

	static importDOM(): DOMConversionMap|null {
		return {
			img: (node: Node) => ({
				conversion: convertInlineImageElement,
				priority: 0,
			}),
		};
	}

	constructor(
		src: string,
		altText: string,
		position: Position,
		width?: 'inherit'|number,
		height?: 'inherit'|number,
		key?: NodeKey,
		classes?: string,

	) {
		super(key);
		this.__src = src;
		this.__altText = altText;
		this.__width = width || 'inherit';
		this.__height = height || 'inherit';
		this.__position = position;
		this.__classes = classes;
	}

	exportDOM(): DOMExportOutput {
		// console.log(this.__position);
		const element = document.createElement('img');

		const prefix = (hasEnv('PUBLIC_ASSET_HOST') ? getEnv('PUBLIC_ASSET_HOST') : '');
		// console.log('prefix', prefix);

		element.setAttribute('src', prefix + this.__src);
		element.setAttribute('class', clsx(
			'inline-block',
			this.__position === 'right' && 'float-right ml-4 mb-4',
			this.__position === 'left' && 'float-left mr-4 mb-4',
			this.__classes
		));
		element.setAttribute('alt', this.__altText);
		element.setAttribute('width', this.__width.toString());
		element.setAttribute('height', this.__height.toString());
		//console.log(element.src, element.width, this.__width, element.height, this.__height);
		return {element};
	}

	exportJSON(): SerializedInlineImageNode {
		return {
			altText: this.getAltText(),
			width: this.__width === 'inherit' ? 0 : this.__width,
			height: this.__height === 'inherit' ? 0 : this.__height,
			position: this.__position,
			src: this.getSrc(),
			type: 'inline-image',
			classes: this.getClasses(),
			version: 1,
		};
	}

	getClasses(): string {
		return this.__classes;
	}

	setClasses(classes: string) {
		const writable = super.getWritable();
		writable.__classes = classes;
	}

	getSrc(): string {
		if (isUrlRelative(this.__src)) {
			return this.__src;
		}

		const url = new URL(this.__src);
		return url.pathname;
	}

	setSrc(src: string): void {
		const writable = this.getWritable();
		writable.__src = src;
	}

	getAltText(): string {
		return this.__altText;
	}

	setAltText(altText: string): void {
		const writable = this.getWritable();
		writable.__altText = altText;
	}

	getWidth(): 'inherit'|number {
		return this.__width;
	}

	setWidth(width: 'inherit'|number): void {
		const writable = this.getWritable();
		writable.__width = width;
	}

	getHeight(): 'inherit'|number {
		return this.__height;
	}

	setHeight(height: 'inherit'|number): void {
		const writable = this.getWritable();
		writable.__height = height;
	}

	getPosition(): Position {
		return this.__position;
	}

	setPosition(position: Position): void {
		const writable = this.getWritable();
		writable.__position = position;
	}

	update(payload: InlineImagePayload): void {
		const writable = this.getWritable();
		const {altText, position, src, width, height, classes} = payload;
		if (altText !== undefined) {
			writable.__altText = altText;
		}
		if (position !== undefined) {
			writable.__position = position;
		}
		if (src !== undefined) {
			writable.__src = src;
		}
		if (width !== undefined) {
			writable.__width = width;
		}
		if (height !== undefined) {
			writable.__height = height;
		}
		if (classes !== undefined) {
			writable.__classes = classes;
		}
	}

	// View

	createDOM(config: EditorConfig): HTMLElement {
		const span = document.createElement('span');
		const className = `${config.theme.inlineImage} position-${this.__position} ${this.__classes}`;
		if (className !== undefined) {
			span.className = className;
		}
		return span;
	}

	updateDOM(prevNode: InlineImageNode, dom: HTMLElement, config: EditorConfig): false {
		const position = this.__position;
		if (position !== prevNode.__position) {
			const className = `${config.theme.inlineImage} position-${position} ${this.__classes}`;
			if (className !== undefined) {
				dom.className = className;
			}
		}
		return false;
	}

	decorate(): JSX.Element {
		return (
			<Suspense fallback={null}>
				<InlineImageComponent
					src={this.__src}
					altText={this.__altText}
					width={this.__width}
					height={this.__height}
					nodeKey={this.getKey()}
					position={this.__position}
					classes={this.__classes}
				/>
			</Suspense>
		);
	}
}

export function $createInlineImageNode({altText, position, height, src, width, key, classes}: InlineImagePayload): InlineImageNode {
	return $applyNodeReplacement( new InlineImageNode(src, altText, position, width, height, key, classes));
}

export function $isInlineImageNode(node: LexicalNode|null|undefined): node is InlineImageNode {
	return node instanceof InlineImageNode;
}
